import React, { forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/utils/cn";

const buttonVariants = cva(
  // Base styles
 "inline-flex items-center justify-center gap-2 font-sans font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] focus-visible:ring-ms-primary-500",
  {
    variants: {
      variant: {
        primary: [
          "text-white hover:-translate-y-0.5 shadow-lg hover:shadow-xl",
 "bg-ms-primary-700 hover:bg-ms-primary-600 shadow-ms-primary-600/20 hover:shadow-ms-primary-600/30",
        ],
        secondary: [
          "border-2 bg-transparent hover:bg-opacity-5",
 "border-ms-primary-600 text-ms-primary-700 hover:border-ms-primary-700 hover:bg-ms-primary-50",
        ],
        ghost: [
          "bg-transparent",
 "text-ms-gray-700 hover:bg-ms-primary-50 hover:text-ms-primary-700",
        ],
        white: [
          "bg-white hover:-translate-y-0.5 shadow-lg hover:shadow-xl",
 "text-ms-primary-700 hover:bg-ms-primary-50",
        ],
        dark: [
          "text-white hover:-translate-y-0.5",
 "bg-ms-gray-800 hover:bg-ms-gray-700",
        ],
        outline: [
          "border bg-transparent",
 "border-ms-gray-300 text-ms-gray-700 hover:border-ms-gray-400 hover:bg-ms-gray-50",
        ],
        link: [
          "underline-offset-4 hover:underline p-0 h-auto",
 "text-ms-primary-700 hover:text-ms-primary-800",
        ],
        // Legacy variants for backwards compatibility
        cancel: "bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-md",
        danger:
          "bg-red-600 text-white hover:bg-red-700 hover:-translate-y-0.5 shadow-lg hover:shadow-xl rounded-md",
      },
      size: {
        sm: "h-10 px-4 text-sm rounded-lg",
        md: "h-12 px-6 text-base rounded-lg",
        lg: "h-14 px-8 text-lg rounded-xl",
        xl: "h-16 px-10 text-lg rounded-xl",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      loadingText,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || isLoading;

    // When asChild is true, Slot requires a single child - don't add loading spinner
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {isLoading && (
          <svg
            className="h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {isLoading && loadingText ? loadingText : children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
